/**
 * GeneratorWizardPage
 *
 * Multi-step wizard for generating advocacy documents.
 * Steps: Type Selection -> Context Input -> Source Selection -> Review & Generate
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Sidebar } from '../components/dashboard';
import { DocumentTypeSelector } from '../components/casemaker';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
  FileText,
  Calendar,
  Info,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { casemakerService } from '../services/casemakerService';
import {
  DocumentGenerationType,
  RecipientInfo,
  DateRange,
  CustomDocumentDetails,
  getDocumentTypeInfo,
  requiresRecipientInfo,
} from '../types/generatedDoc';

// Generation progress messages shown during document generation
const GENERATION_PROGRESS_MESSAGES = [
  { delay: 0, message: 'Starting generation...' },
  { delay: 3000, message: 'Analyzing your situation...' },
  { delay: 8000, message: 'Reviewing source documents...' },
  { delay: 15000, message: 'Generating narrative...' },
  { delay: 25000, message: 'Formatting document...' },
  { delay: 40000, message: 'Almost done...' },
  { delay: 55000, message: 'Finalizing content...' },
];

// Timeout for generation (70 seconds to allow for slow responses)
const GENERATION_TIMEOUT_MS = 70000;

interface Document {
  documentId: string;
  fileName: string;
  category: string;
}

interface CareLog {
  logId: string;
  logText: string;
  logDate: string;
}

interface Deadline {
  deadlineId: string;
  title: string;
  dueDate: string;
}

interface ListDocumentsResponse {
  success: boolean;
  documents: Document[];
  error?: string;
}

interface ListCareLogsResponse {
  success: boolean;
  careLogs: CareLog[];
  error?: string;
}

interface ListDeadlinesResponse {
  success: boolean;
  deadlines: Deadline[];
  error?: string;
}

type WizardStep = 'type' | 'context' | 'sources' | 'review';

const STEPS: Array<{ id: WizardStep; label: string }> = [
  { id: 'type', label: 'Document Type' },
  { id: 'context', label: 'Details' },
  { id: 'sources', label: 'Sources' },
  { id: 'review', label: 'Generate' },
];

export function GeneratorWizardPage(): JSX.Element {
  const { caseFileId } = useParams<{ caseFileId: string }>();
  const navigate = useNavigate();

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<WizardStep>('type');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState('');
  const progressTimersRef = useRef<NodeJS.Timeout[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Form state
  const [documentType, setDocumentType] = useState<DocumentGenerationType | null>(null);
  const [userDescription, setUserDescription] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [title, setTitle] = useState('');
  const [recipientInfo, setRecipientInfo] = useState<RecipientInfo>({});
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: '', endDate: '' });

  // Appointment prep state
  const [appointmentType, setAppointmentType] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [providerName, setProviderName] = useState('');
  const [logsFrom, setLogsFrom] = useState('');
  const [logsThrough, setLogsThrough] = useState('');

  // Custom document state
  const [customDetails, setCustomDetails] = useState<CustomDocumentDetails>({
    title: '',
    purpose: '',
    recipient: '',
    recipientRole: '',
    tone: 'professional',
    keyPoints: '',
    desiredOutcome: '',
  });

  // Source selection state
  const [availableDocuments, setAvailableDocuments] = useState<Document[]>([]);
  const [availableCareLogs, setAvailableCareLogs] = useState<CareLog[]>([]);
  const [availableDeadlines, setAvailableDeadlines] = useState<Deadline[]>([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [selectedCareLogIds, setSelectedCareLogIds] = useState<string[]>([]);
  const [selectedDeadlineId, setSelectedDeadlineId] = useState<string | undefined>();
  const [loadingSources, setLoadingSources] = useState(false);

  // Load available sources when entering sources step
  const loadSources = useCallback(async () => {
    if (!caseFileId) return;

    setLoadingSources(true);
    try {
      // Load documents
      const listDocuments = httpsCallable<{ caseFileId: string }, ListDocumentsResponse>(
        functions,
        'listDocuments'
      );
      const docsResult = await listDocuments({ caseFileId });
      if (docsResult.data.success) {
        setAvailableDocuments(docsResult.data.documents);
      }

      // Load care logs
      const listCareLogs = httpsCallable<
        { caseFileId: string; limit?: number },
        ListCareLogsResponse
      >(functions, 'listCareLogs');
      const logsResult = await listCareLogs({ caseFileId, limit: 50 });
      if (logsResult.data.success) {
        setAvailableCareLogs(logsResult.data.careLogs);
      }

      // Load deadlines
      const listDeadlines = httpsCallable<
        { caseFileId: string; limit?: number },
        ListDeadlinesResponse
      >(functions, 'listDeadlines');
      const deadlinesResult = await listDeadlines({ caseFileId, limit: 20 });
      if (deadlinesResult.data.success) {
        setAvailableDeadlines(deadlinesResult.data.deadlines);
      }
    } catch (err) {
      console.error('Error loading sources:', err);
    } finally {
      setLoadingSources(false);
    }
  }, [caseFileId]);

  useEffect(() => {
    if (currentStep === 'sources') {
      loadSources();
    }
  }, [currentStep, loadSources]);

  const isAppointmentPrep = documentType === 'appointment_prep';

  // Auto-populate log date range when appointment date changes
  const handleAppointmentDateChange = (date: string) => {
    setAppointmentDate(date);
    if (date) {
      const apptDate = new Date(date + 'T00:00:00');
      const thirtyDaysBefore = new Date(apptDate);
      thirtyDaysBefore.setDate(thirtyDaysBefore.getDate() - 30);
      setLogsFrom(thirtyDaysBefore.toISOString().split('T')[0]);
      setLogsThrough(date);
    }
  };

  const getCurrentStepIndex = () => STEPS.findIndex((s) => s.id === currentStep);

  const isCustomDoc = documentType === 'custom';

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 'type':
        return documentType !== null;
      case 'context':
        if (isCustomDoc) {
          return (
            customDetails.title.trim().length > 0 &&
            customDetails.purpose.trim().length > 0 &&
            customDetails.keyPoints.trim().length > 0 &&
            customDetails.desiredOutcome.trim().length > 0
          );
        }
        if (isAppointmentPrep) {
          return (
            appointmentType.trim().length > 0 &&
            appointmentDate.trim().length > 0 &&
            providerName.trim().length > 0 &&
            logsFrom.trim().length > 0 &&
            logsThrough.trim().length > 0
          );
        }
        return userDescription.trim().length > 0;
      case 'sources':
        return true; // Sources are optional
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    const idx = getCurrentStepIndex();
    if (idx < STEPS.length - 1 && canProceed()) {
      setCurrentStep(STEPS[idx + 1].id);
    }
  };

  const handleBack = () => {
    const idx = getCurrentStepIndex();
    if (idx > 0) {
      setCurrentStep(STEPS[idx - 1].id);
    }
  };

  // Clear progress timers when component unmounts or generation completes
  const clearProgressTimers = useCallback(() => {
    progressTimersRef.current.forEach(timer => clearTimeout(timer));
    progressTimersRef.current = [];
    setProgressMessage('');
  }, []);

  // Start progress message sequence
  const startProgressMessages = useCallback(() => {
    clearProgressTimers();
    GENERATION_PROGRESS_MESSAGES.forEach(({ delay, message }) => {
      const timer = setTimeout(() => {
        setProgressMessage(message);
      }, delay);
      progressTimersRef.current.push(timer);
    });
  }, [clearProgressTimers]);

  // Parse error messages for user-friendly display
  const parseErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      // Handle timeout errors
      if (message.includes('timeout') || message.includes('timed out')) {
        return 'The request took too long. Please try again. If the problem persists, try with fewer source documents.';
      }

      // Handle network errors
      if (message.includes('network') || message.includes('fetch') || message.includes('failed to fetch')) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      }

      // Handle authentication errors
      if (message.includes('unauthorized') || message.includes('unauthenticated') || message.includes('401')) {
        return 'Your session has expired. Please refresh the page and log in again.';
      }

      // Handle rate limiting
      if (message.includes('rate limit') || message.includes('too many requests') || message.includes('429')) {
        return 'Too many requests. Please wait a moment and try again.';
      }

      // Handle server errors
      if (message.includes('500') || message.includes('internal server error')) {
        return 'The server encountered an error. Please try again in a few moments.';
      }

      // Return the original message if it's reasonably short and user-friendly
      if (error.message.length < 150 && !message.includes('exception')) {
        return error.message;
      }

      return 'An unexpected error occurred. Please try again.';
    }

    return 'Failed to generate document. Please try again.';
  };

  const handleGenerate = async () => {
    if (!caseFileId || !documentType) return;

    setIsGenerating(true);
    setError(null);
    startProgressMessages();

    // Create abort controller for timeout
    abortControllerRef.current = new AbortController();
    const timeoutId = setTimeout(() => {
      abortControllerRef.current?.abort();
    }, GENERATION_TIMEOUT_MS);

    try {
      // For custom documents, build userDescription from purpose + key points
      const effectiveDescription = isCustomDoc
        ? `Purpose: ${customDetails.purpose}\n\nKey Points: ${customDetails.keyPoints}\n\nDesired Outcome: ${customDetails.desiredOutcome}`
        : isAppointmentPrep
          ? (userDescription || `Preparing for ${appointmentType} with ${providerName}`)
          : userDescription;

      const response = await casemakerService.generateDocument({
        caseFileId,
        documentType,
        userDescription: effectiveDescription,
        additionalContext: additionalContext || undefined,
        title: isCustomDoc ? customDetails.title : (title || undefined),
        sourceDocumentIds: selectedDocumentIds.length > 0 ? selectedDocumentIds : undefined,
        sourceCareLogIds: selectedCareLogIds.length > 0 ? selectedCareLogIds : undefined,
        sourceDeadlineId: selectedDeadlineId,
        recipientInfo: requiresRecipientInfo(documentType) ? recipientInfo : undefined,
        dateRange: dateRange.startDate && dateRange.endDate ? dateRange : undefined,
        // Appointment prep fields
        dateRangeStart: isAppointmentPrep ? logsFrom : undefined,
        dateRangeEnd: isAppointmentPrep ? logsThrough : undefined,
        appointmentType: isAppointmentPrep ? appointmentType : undefined,
        appointmentDate: isAppointmentPrep ? appointmentDate : undefined,
        providerName: isAppointmentPrep ? providerName : undefined,
        customDocumentDetails: isCustomDoc ? customDetails : undefined,
      });

      clearTimeout(timeoutId);
      clearProgressTimers();

      if (response.success && response.docId) {
        // Navigate to the document editor
        navigate(`/casefile/${caseFileId}/casemaker/${response.docId}`);
      } else {
        setError(response.error || 'Failed to generate document. Please try again.');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      clearProgressTimers();

      // Check if it was an abort (timeout)
      if (err instanceof Error && err.name === 'AbortError') {
        setError('The request timed out. Document generation can take up to 60 seconds. Please try again.');
      } else {
        setError(parseErrorMessage(err));
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearProgressTimers();
      abortControllerRef.current?.abort();
    };
  }, [clearProgressTimers]);

  const handleBackToCaseMaker = () => {
    navigate(`/casefile/${caseFileId}/casemaker`);
  };

  const typeInfo = documentType ? getDocumentTypeInfo(documentType) : null;
  const showRecipientInfo = documentType ? requiresRecipientInfo(documentType) : false;

  if (!caseFileId) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <div className="card p-8 max-w-md text-center">
          <h2 className="text-lg font-semibold text-ink mb-2">No Care Record Selected</h2>
          <p className="text-muted mb-6">Please select a care record from the dashboard to create a document.</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-airy flex">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
      </div>

      {/* Sidebar - Mobile Overlay */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-ink/40 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar className="h-full" activeCaseFileId={caseFileId} />
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-soft-neutral sticky top-0 z-10">
          <div className="px-4 lg:px-6 py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBackToCaseMaker}
                className="btn-ghost btn-sm p-1"
                aria-label="Back to CaseMaker"
              >
                <ArrowLeft size={20} aria-hidden="true" />
              </button>
              <h1 className="text-xl font-semibold text-ink">Create Document</h1>
            </div>

            {/* Step Indicator */}
            <div className="mt-4 flex items-center justify-between max-w-2xl">
              {STEPS.map((step, idx) => {
                const currentIdx = getCurrentStepIndex();
                const isComplete = idx < currentIdx;
                const isCurrent = idx === currentIdx;

                return (
                  <div key={step.id} className="flex items-center">
                    <div className="flex items-center gap-2">
                      <div
                        className={`
                          w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                          ${isComplete ? 'bg-evergreen text-white' : ''}
                          ${isCurrent ? 'bg-evergreen text-white' : ''}
                          ${!isComplete && !isCurrent ? 'bg-soft-neutral text-muted' : ''}
                        `}
                      >
                        {isComplete ? <Check size={16} /> : idx + 1}
                      </div>
                      <span
                        className={`text-sm hidden sm:inline ${
                          isCurrent ? 'text-ink font-medium' : 'text-muted'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div
                        className={`w-8 sm:w-16 h-0.5 mx-2 ${
                          isComplete ? 'bg-evergreen' : 'bg-soft-neutral'
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div id="main-content" role="main" tabIndex={-1} className="flex-1 p-4 lg:p-6 outline-none">
          <div className="max-w-3xl mx-auto">
            {/* Step 1: Document Type Selection */}
            {currentStep === 'type' && (
              <DocumentTypeSelector
                selectedType={documentType}
                onSelect={setDocumentType}
                onFillFormClick={() => navigate(`/casefile/${caseFileId}/casemaker/form-filler`)}
              />
            )}

            {/* Step 2: Context Input */}
            {currentStep === 'context' && typeInfo && !isCustomDoc && !isAppointmentPrep && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-ink mb-2">
                    Tell us about your situation
                  </h2>
                  <p className="text-muted">
                    Provide details to help generate a {typeInfo.name}
                  </p>
                </div>

                {/* Prompt Hints */}
                {typeInfo.promptHints.length > 0 && (
                  <div className="card bg-sage/30 border-sage">
                    <div className="card-body">
                      <div className="flex items-start gap-3">
                        <Info className="text-evergreen flex-shrink-0 mt-0.5" size={20} />
                        <div>
                          <p className="font-medium text-ink mb-2">Questions to consider:</p>
                          <ul className="space-y-1 text-sm text-muted">
                            {typeInfo.promptHints.map((hint, idx) => (
                              <li key={idx}>- {hint}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Main Description */}
                <div>
                  <label htmlFor="gen-std-description" className="label">
                    Describe your situation <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="gen-std-description"
                    value={userDescription}
                    onChange={(e) => setUserDescription(e.target.value)}
                    placeholder="Explain your situation in your own words. What happened? What do you need?"
                    className="textarea w-full h-40"
                  />
                  <p className="text-xs text-muted mt-1">
                    This helps the AI understand your specific needs
                  </p>
                </div>

                {/* Additional Context */}
                <div>
                  <label htmlFor="gen-std-additional" className="label">Additional context (optional)</label>
                  <textarea
                    id="gen-std-additional"
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder="Any other relevant information, deadlines, or special circumstances..."
                    className="textarea w-full h-24"
                  />
                </div>

                {/* Custom Title */}
                <div>
                  <label htmlFor="gen-std-title" className="label">Document title (optional)</label>
                  <input
                    id="gen-std-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={`${typeInfo.name} - ${new Date().toLocaleDateString()}`}
                    className="input w-full"
                  />
                </div>

                {/* Recipient Info (for letters) */}
                {showRecipientInfo && (
                  <div className="card">
                    <div className="card-header">
                      <h3 className="font-medium text-ink">Recipient Information</h3>
                    </div>
                    <div className="card-body space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="gen-std-recipient-name" className="label">Name</label>
                          <input
                            id="gen-std-recipient-name"
                            type="text"
                            value={recipientInfo.name || ''}
                            onChange={(e) =>
                              setRecipientInfo({ ...recipientInfo, name: e.target.value })
                            }
                            placeholder="John Smith"
                            className="input w-full"
                          />
                        </div>
                        <div>
                          <label htmlFor="gen-std-recipient-title" className="label">Title</label>
                          <input
                            id="gen-std-recipient-title"
                            type="text"
                            value={recipientInfo.title || ''}
                            onChange={(e) =>
                              setRecipientInfo({ ...recipientInfo, title: e.target.value })
                            }
                            placeholder="Claims Manager"
                            className="input w-full"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="gen-std-recipient-org" className="label">Organization</label>
                        <input
                          id="gen-std-recipient-org"
                          type="text"
                          value={recipientInfo.organization || ''}
                          onChange={(e) =>
                            setRecipientInfo({ ...recipientInfo, organization: e.target.value })
                          }
                          placeholder="ABC Insurance Company"
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label htmlFor="gen-std-recipient-address" className="label">Address</label>
                        <textarea
                          id="gen-std-recipient-address"
                          value={recipientInfo.address || ''}
                          onChange={(e) =>
                            setRecipientInfo({ ...recipientInfo, address: e.target.value })
                          }
                          placeholder="123 Main St&#10;City, State 12345"
                          className="textarea w-full h-20"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="gen-std-recipient-email" className="label">Email</label>
                          <input
                            id="gen-std-recipient-email"
                            type="email"
                            value={recipientInfo.email || ''}
                            onChange={(e) =>
                              setRecipientInfo({ ...recipientInfo, email: e.target.value })
                            }
                            placeholder="claims@insurance.com"
                            className="input w-full"
                          />
                        </div>
                        <div>
                          <label htmlFor="gen-std-recipient-fax" className="label">Fax Number</label>
                          <input
                            id="gen-std-recipient-fax"
                            type="text"
                            value={recipientInfo.faxNumber || ''}
                            onChange={(e) =>
                              setRecipientInfo({ ...recipientInfo, faxNumber: e.target.value })
                            }
                            placeholder="(555) 123-4567"
                            className="input w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Appointment Prep Details */}
            {currentStep === 'context' && isAppointmentPrep && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-ink mb-2">
                    Appointment Details
                  </h2>
                  <p className="text-muted">
                    Tell us about the upcoming appointment so we can compile a prep document
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="gen-appt-type" className="label">
                      Appointment Type <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="gen-appt-type"
                      type="text"
                      value={appointmentType}
                      onChange={(e) => setAppointmentType(e.target.value)}
                      placeholder="e.g., Pediatric Neurology, IEP Meeting, Primary Care"
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="gen-appt-provider" className="label">
                      Provider / Team Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="gen-appt-provider"
                      type="text"
                      value={providerName}
                      onChange={(e) => setProviderName(e.target.value)}
                      placeholder="e.g., Dr. Chen, IEP Team"
                      className="input w-full"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="gen-appt-date" className="label">
                    Appointment Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="gen-appt-date"
                    type="date"
                    value={appointmentDate}
                    onChange={(e) => handleAppointmentDateChange(e.target.value)}
                    className="input w-full sm:w-64"
                  />
                  <p className="text-xs text-muted mt-1">
                    Setting this auto-fills the log date range below
                  </p>
                </div>

                <div className="card">
                  <div className="card-header flex items-center gap-2">
                    <Calendar size={18} className="text-evergreen" />
                    <h3 className="font-medium text-ink">Care Log Date Range</h3>
                  </div>
                  <div className="card-body">
                    <p className="text-sm text-muted mb-3">
                      Include care logs from this period in the prep document. Defaults to 30 days before the appointment.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="gen-appt-logs-from" className="label">
                          Logs from <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="gen-appt-logs-from"
                          type="date"
                          value={logsFrom}
                          onChange={(e) => setLogsFrom(e.target.value)}
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label htmlFor="gen-appt-logs-through" className="label">
                          Logs through <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="gen-appt-logs-through"
                          type="date"
                          value={logsThrough}
                          onChange={(e) => setLogsThrough(e.target.value)}
                          className="input w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Optional additional notes */}
                <div>
                  <label htmlFor="gen-appt-notes" className="label">Additional notes for the visit (optional)</label>
                  <textarea
                    id="gen-appt-notes"
                    value={userDescription}
                    onChange={(e) => setUserDescription(e.target.value)}
                    placeholder="Any specific concerns, recent changes, or things you want to make sure are covered..."
                    className="textarea w-full h-24"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Custom Document Details */}
            {currentStep === 'context' && isCustomDoc && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-ink mb-2">
                    Describe your document
                  </h2>
                  <p className="text-muted">
                    Tell us what you need and the AI will draft it for you
                  </p>
                </div>

                {/* Document Title */}
                <div>
                  <label htmlFor="gen-custom-title" className="label">
                    Document title <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="gen-custom-title"
                    type="text"
                    value={customDetails.title}
                    onChange={(e) =>
                      setCustomDetails({ ...customDetails, title: e.target.value })
                    }
                    placeholder="e.g., Letter to School Principal, Therapy Coverage Request"
                    className="input w-full"
                  />
                  <p className="text-xs text-muted mt-1">
                    What should this document be called?
                  </p>
                </div>

                {/* Document Purpose */}
                <div>
                  <label htmlFor="gen-custom-purpose" className="label">
                    Document purpose <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="gen-custom-purpose"
                    value={customDetails.purpose}
                    onChange={(e) =>
                      setCustomDetails({ ...customDetails, purpose: e.target.value })
                    }
                    placeholder="e.g., Request a meeting with the school principal about bullying incidents and request an updated safety plan"
                    className="textarea w-full h-28"
                  />
                  <p className="text-xs text-muted mt-1">
                    What are you trying to accomplish with this document?
                  </p>
                </div>

                {/* Recipient */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="gen-custom-recipient" className="label">Recipient (optional)</label>
                    <input
                      id="gen-custom-recipient"
                      type="text"
                      value={customDetails.recipient}
                      onChange={(e) =>
                        setCustomDetails({ ...customDetails, recipient: e.target.value })
                      }
                      placeholder="e.g., Dr. Sarah Johnson, School Board"
                      className="input w-full"
                    />
                    <p className="text-xs text-muted mt-1">
                      Who will receive this document?
                    </p>
                  </div>
                  <div>
                    <label htmlFor="gen-custom-recipient-role" className="label">Recipient role/title (optional)</label>
                    <input
                      id="gen-custom-recipient-role"
                      type="text"
                      value={customDetails.recipientRole || ''}
                      onChange={(e) =>
                        setCustomDetails({ ...customDetails, recipientRole: e.target.value })
                      }
                      placeholder="e.g., Principal, Case Manager"
                      className="input w-full"
                    />
                    <p className="text-xs text-muted mt-1">
                      Their position or organization
                    </p>
                  </div>
                </div>

                {/* Tone */}
                <div>
                  <label htmlFor="gen-custom-tone" className="label">Tone</label>
                  <select
                    id="gen-custom-tone"
                    value={customDetails.tone}
                    onChange={(e) =>
                      setCustomDetails({
                        ...customDetails,
                        tone: e.target.value as CustomDocumentDetails['tone'],
                      })
                    }
                    className="input w-full"
                  >
                    <option value="formal">Formal - Traditional business language</option>
                    <option value="professional">Professional - Clear and courteous</option>
                    <option value="assertive">Assertive - Direct and firm</option>
                    <option value="collaborative">Collaborative - Warm and solution-oriented</option>
                  </select>
                </div>

                {/* Key Points */}
                <div>
                  <label htmlFor="gen-custom-keypoints" className="label">
                    Key points to include <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="gen-custom-keypoints"
                    value={customDetails.keyPoints}
                    onChange={(e) =>
                      setCustomDetails({ ...customDetails, keyPoints: e.target.value })
                    }
                    placeholder="List the specific information, requests, or arguments that should be included in the document..."
                    className="textarea w-full h-32"
                  />
                  <p className="text-xs text-muted mt-1">
                    What specific information or requests should be included?
                  </p>
                </div>

                {/* Desired Outcome */}
                <div>
                  <label htmlFor="gen-custom-outcome" className="label">
                    Desired outcome <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="gen-custom-outcome"
                    value={customDetails.desiredOutcome}
                    onChange={(e) =>
                      setCustomDetails({ ...customDetails, desiredOutcome: e.target.value })
                    }
                    placeholder="e.g., Schedule a meeting within 2 weeks, get approval for the requested service, receive a written response..."
                    className="textarea w-full h-24"
                  />
                  <p className="text-xs text-muted mt-1">
                    What response or action are you hoping for?
                  </p>
                </div>

                {/* Additional Context */}
                <div>
                  <label htmlFor="gen-custom-additional" className="label">Additional context (optional)</label>
                  <textarea
                    id="gen-custom-additional"
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder="Any other relevant information, deadlines, or special circumstances..."
                    className="textarea w-full h-24"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Source Selection */}
            {currentStep === 'sources' && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-ink mb-2">
                    Select supporting information
                  </h2>
                  <p className="text-muted">
                    Include documents, care logs, or deadlines to strengthen your document
                  </p>
                </div>

                {loadingSources ? (
                  <div className="card">
                    <div className="card-body text-center py-8">
                      <div className="animate-pulse">
                        <FileText className="mx-auto mb-2 text-muted" size={32} />
                        <p className="text-muted">Loading available sources...</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Date Range Filter */}
                    <div className="card">
                      <div className="card-header flex items-center gap-2">
                        <Calendar size={18} className="text-evergreen" />
                        <h3 className="font-medium text-ink">Date Range (optional)</h3>
                      </div>
                      <div className="card-body">
                        <p className="text-sm text-muted mb-3">
                          Filter care logs and documents by date range
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="gen-src-date-from" className="label">From</label>
                            <input
                              id="gen-src-date-from"
                              type="date"
                              value={dateRange.startDate}
                              onChange={(e) =>
                                setDateRange({ ...dateRange, startDate: e.target.value })
                              }
                              className="input w-full"
                            />
                          </div>
                          <div>
                            <label htmlFor="gen-src-date-to" className="label">To</label>
                            <input
                              id="gen-src-date-to"
                              type="date"
                              value={dateRange.endDate}
                              onChange={(e) =>
                                setDateRange({ ...dateRange, endDate: e.target.value })
                              }
                              className="input w-full"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Documents */}
                    <div className="card">
                      <div className="card-header flex items-center justify-between">
                        <h3 className="font-medium text-ink">
                          Documents ({selectedDocumentIds.length} selected)
                        </h3>
                        {selectedDocumentIds.length > 0 && (
                          <button
                            onClick={() => setSelectedDocumentIds([])}
                            className="btn-ghost btn-sm text-muted"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="card-body">
                        {availableDocuments.length === 0 ? (
                          <p className="text-sm text-muted">No documents available</p>
                        ) : (
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {availableDocuments.map((doc) => (
                              <label
                                key={doc.documentId}
                                className="flex items-center gap-3 p-2 rounded hover:bg-sage/30 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDocumentIds.includes(doc.documentId)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDocumentIds([...selectedDocumentIds, doc.documentId]);
                                    } else {
                                      setSelectedDocumentIds(
                                        selectedDocumentIds.filter((id) => id !== doc.documentId)
                                      );
                                    }
                                  }}
                                  className="checkbox"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-ink truncate">
                                    {doc.fileName}
                                  </p>
                                  <p className="text-xs text-muted">{doc.category}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Care Logs */}
                    <div className="card">
                      <div className="card-header flex items-center justify-between">
                        <h3 className="font-medium text-ink">
                          Care Logs ({selectedCareLogIds.length} selected)
                        </h3>
                        {selectedCareLogIds.length > 0 && (
                          <button
                            onClick={() => setSelectedCareLogIds([])}
                            className="btn-ghost btn-sm text-muted"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="card-body">
                        {availableCareLogs.length === 0 ? (
                          <p className="text-sm text-muted">No care logs available</p>
                        ) : (
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {availableCareLogs.map((log) => (
                              <label
                                key={log.logId}
                                className="flex items-start gap-3 p-2 rounded hover:bg-sage/30 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedCareLogIds.includes(log.logId)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedCareLogIds([...selectedCareLogIds, log.logId]);
                                    } else {
                                      setSelectedCareLogIds(
                                        selectedCareLogIds.filter((id) => id !== log.logId)
                                      );
                                    }
                                  }}
                                  className="checkbox mt-1"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-muted">{log.logDate}</p>
                                  <p className="text-sm text-ink line-clamp-2">{log.logText}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Deadlines */}
                    <div className="card">
                      <div className="card-header flex items-center justify-between">
                        <h3 className="font-medium text-ink">Related Deadline</h3>
                        {selectedDeadlineId && (
                          <button
                            onClick={() => setSelectedDeadlineId(undefined)}
                            className="btn-ghost btn-sm text-muted"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="card-body">
                        {availableDeadlines.length === 0 ? (
                          <p className="text-sm text-muted">No deadlines available</p>
                        ) : (
                          <div className="space-y-2 max-h-36 overflow-y-auto">
                            {availableDeadlines.map((deadline) => (
                              <label
                                key={deadline.deadlineId}
                                className="flex items-center gap-3 p-2 rounded hover:bg-sage/30 cursor-pointer"
                              >
                                <input
                                  type="radio"
                                  name="deadline"
                                  checked={selectedDeadlineId === deadline.deadlineId}
                                  onChange={() => setSelectedDeadlineId(deadline.deadlineId)}
                                  className="radio"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-ink">{deadline.title}</p>
                                  <p className="text-xs text-muted">Due: {deadline.dueDate}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 4: Review & Generate */}
            {currentStep === 'review' && (typeInfo || isCustomDoc || isAppointmentPrep) && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-ink mb-2">
                    Ready to generate
                  </h2>
                  <p className="text-muted">Review your selections before generating</p>
                </div>

                {/* Summary Card */}
                <div className="card">
                  <div className="card-body space-y-4">
                    <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                      <span className="text-muted">Document Type</span>
                      <span className="font-medium text-ink">
                        {isCustomDoc ? 'Custom Document' : typeInfo?.name}
                      </span>
                    </div>
                    {isAppointmentPrep ? (
                      <>
                        <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                          <span className="text-muted">Visit Type</span>
                          <span className="font-medium text-ink">{appointmentType}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                          <span className="text-muted">Provider</span>
                          <span className="font-medium text-ink">{providerName}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                          <span className="text-muted">Appointment Date</span>
                          <span className="font-medium text-ink">{appointmentDate}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                          <span className="text-muted">Log Period</span>
                          <span className="font-medium text-ink">{logsFrom} to {logsThrough}</span>
                        </div>
                        {userDescription && (
                          <div className="py-2 border-b border-soft-neutral">
                            <span className="text-muted block mb-2">Additional Notes</span>
                            <p className="text-sm text-ink">{userDescription}</p>
                          </div>
                        )}
                      </>
                    ) : isCustomDoc ? (
                      <>
                        <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                          <span className="text-muted">Title</span>
                          <span className="font-medium text-ink">{customDetails.title}</span>
                        </div>
                        <div className="py-2 border-b border-soft-neutral">
                          <span className="text-muted block mb-2">Purpose</span>
                          <p className="text-sm text-ink">{customDetails.purpose}</p>
                        </div>
                        {customDetails.recipient && (
                          <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                            <span className="text-muted">Recipient</span>
                            <span className="font-medium text-ink">
                              {customDetails.recipient}
                              {customDetails.recipientRole && ` (${customDetails.recipientRole})`}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                          <span className="text-muted">Tone</span>
                          <span className="font-medium text-ink capitalize">{customDetails.tone}</span>
                        </div>
                        <div className="py-2 border-b border-soft-neutral">
                          <span className="text-muted block mb-2">Key Points</span>
                          <p className="text-sm text-ink">{customDetails.keyPoints}</p>
                        </div>
                        <div className="py-2 border-b border-soft-neutral">
                          <span className="text-muted block mb-2">Desired Outcome</span>
                          <p className="text-sm text-ink">{customDetails.desiredOutcome}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        {title && (
                          <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                            <span className="text-muted">Title</span>
                            <span className="font-medium text-ink">{title}</span>
                          </div>
                        )}
                        <div className="py-2 border-b border-soft-neutral">
                          <span className="text-muted block mb-2">Description</span>
                          <p className="text-sm text-ink">{userDescription}</p>
                        </div>
                      </>
                    )}
                    {additionalContext && (
                      <div className="py-2 border-b border-soft-neutral">
                        <span className="text-muted block mb-2">Additional Context</span>
                        <p className="text-sm text-ink">{additionalContext}</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                      <span className="text-muted">Documents</span>
                      <span className="font-medium text-ink">
                        {selectedDocumentIds.length} selected
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                      <span className="text-muted">Care Logs</span>
                      <span className="font-medium text-ink">
                        {selectedCareLogIds.length} selected
                      </span>
                    </div>
                    {selectedDeadlineId && (
                      <div className="flex items-center justify-between py-2 border-b border-soft-neutral">
                        <span className="text-muted">Deadline</span>
                        <span className="font-medium text-ink">1 linked</span>
                      </div>
                    )}
                    {showRecipientInfo && recipientInfo.name && (
                      <div className="py-2 border-b border-soft-neutral">
                        <span className="text-muted block mb-2">Recipient</span>
                        <p className="text-sm text-ink">
                          {recipientInfo.name}
                          {recipientInfo.title && `, ${recipientInfo.title}`}
                          {recipientInfo.organization && (
                            <span className="block">{recipientInfo.organization}</span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="card border-red-200 bg-red-50" role="alert">
                    <div className="card-body flex items-start gap-3">
                      <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} aria-hidden="true" />
                      <div>
                        <p className="font-medium text-red-800 mb-1">Generation Failed</p>
                        <p className="text-red-700 text-sm">{error}</p>
                        <button
                          onClick={() => setError(null)}
                          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Generate Button and Progress */}
                {isGenerating ? (
                  <div className="card border-evergreen bg-sage/30">
                    <div className="card-body text-center py-8">
                      <div className="flex flex-col items-center gap-4">
                        <div className="relative">
                          <Loader2 className="w-12 h-12 text-evergreen animate-spin" />
                          <Sparkles className="absolute inset-0 m-auto w-5 h-5 text-evergreen" />
                        </div>
                        <div>
                          <p className="font-medium text-ink mb-1">
                            {progressMessage || 'Starting generation...'}
                          </p>
                          <p className="text-sm text-muted">
                            This typically takes 30-60 seconds
                          </p>
                        </div>
                        {/* Progress bar animation */}
                        <div className="w-full max-w-xs h-2 bg-soft-neutral rounded-full overflow-hidden">
                          <div className="h-full bg-evergreen rounded-full animate-progress-indeterminate" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={handleGenerate}
                      className="btn-primary w-full py-4 flex items-center justify-center gap-2"
                    >
                      <Sparkles size={20} />
                      Generate Document
                    </button>

                    <p className="text-xs text-center text-muted">
                      Generation typically takes 30-60 seconds. You can edit the result after.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-soft-neutral">
              <button
                onClick={handleBack}
                disabled={currentStep === 'type'}
                className="btn-ghost flex items-center gap-2"
              >
                <ArrowLeft size={18} />
                Back
              </button>
              {currentStep !== 'review' && (
                <button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="btn-primary flex items-center gap-2"
                >
                  Next
                  <ArrowRight size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneratorWizardPage;
